// PD-Konflikt im Partner Directory: Hook für den Zustand und Abschnitt für den Details-Dialog
// der Transportfreigabe (Vertrag v28, PD-Konflikt-Workflow mit Edge-Unterstützung).
// Laufanzeige: gespeicherter Planstatus über die gemeinsame SSE-Verbindung.
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, LinearProgress, Checkbox, FormControlLabel, Radio, RadioGroup, Stack, Typography,
} from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { MonitorButton } from "../../components/MonitorControls";
import { api, getToken, v } from "../../utils/helpers";
import useCicdEvents from "../../hooks/useCicdEvents";


const currentToken = () => { try { return getToken(); } catch { return null; } };

const ACTIVE_RUN_STATUSES = new Set(["queued", "in_progress", "waiting", "requested", "pending"]);

const RADIO_SX = { color: "var(--iio-text-2)", "&.Mui-checked": { color: "var(--iio-signal)" } };

/** Baut die Query-Parameter für den run-status-Endpunkt zusammen. */
function runStatusQuery(spaceId, packageId, resolveRunId, planId, local = true, transportRunId) {
  const params = new URLSearchParams({ spaceId, packageId });
  if (local) params.set("local", "1");
  if (transportRunId) params.set("transportRunId", String(transportRunId));
  if (resolveRunId) params.set("resolveRunId", String(resolveRunId));
  if (planId) params.set("planId", planId);
  return params.toString();
}

/**
 * Hook: lädt und verwaltet PD-Konflikte für einen fehlgeschlagenen Lauf.
 * Einzige Quelle des Zustands je Karte (kein zweiter Fetch-Loop).
 */
export function usePdConflicts({ spaceId, packageId, runId, enabled = true }) {
  const authToken = currentToken();
  const [conflicts, setConflicts] = useState(null);
  const [canResolve, setCanResolve] = useState(false);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState(null);
  const [readWarning, setReadWarning] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const readController = useRef(null);
  const readVersion = useRef(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [targetStage, setTargetStage] = useState(null);
  // Ergebnis des letzten abgeschlossenen Auflösungslaufs, getrennt vom Transportergebnis.
  const [lastResolveStatus, setLastResolveStatus] = useState(null);
  const mountedRef = useRef(true);
  // Lauf-ID oder Plan-ID des gestarteten Auflösungslaufs.
  const resolveRunRef = useRef(null);
  const planIdRef = useRef(null);
  const completedReadRef = useRef(null);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; readController.current?.abort(); readVersion.current += 1; }; }, []);

  const fetchConflicts = useCallback(async ({ resolvedStatus = null, completionPlanId = null } = {}) => {
    if (!mountedRef.current || !spaceId || !packageId || !runId || !enabled) return;
    readController.current?.abort();
    const controller = new AbortController();
    readController.current = controller;
    const version = ++readVersion.current;
    const active = () => mountedRef.current && !controller.signal.aborted && version === readVersion.current && currentToken() === authToken;
    const readOptions = { signal: controller.signal, retry: false };
    setReadError(null);
    setReadWarning(null);
    setStatusError(null);
    setLoading(true);
    try {
      const data = await api(`/api/cicd/pd-conflicts?spaceId=${encodeURIComponent(spaceId)}&packageId=${encodeURIComponent(packageId)}&runId=${encodeURIComponent(runId)}${completionPlanId ? `&completionPlanId=${encodeURIComponent(completionPlanId)}` : ""}`, readOptions);
      if (!active()) return;
      setReadWarning(data.pdErrors?.length ? {
        code: "PD_READ_INCOMPLETE", issues: data.pdErrors, runtimeChecks: data.runtimeChecks || [],
      } : null);
      const list = data.conflicts || [];
      setConflicts(list);
      setCanResolve(data.canResolve === true && list.length > 0);
      setTargetStage(data.targetStage || null);
      if (list.length === 0) setRunning(false);

      // lastResolve: wenn vorhanden, einmal den Status abfragen (Vertrag: Laufanzeige beim Laden).
      const lr = data.lastResolve;
      if (resolvedStatus) {
        setLastResolveStatus(resolvedStatus);
      } else if (lr) {
        resolveRunRef.current = lr.resolveRunId || null;
        planIdRef.current = lr.planId || null;
        const qs = runStatusQuery(spaceId, packageId, lr.resolveRunId, lr.planId, true, runId);
        try {
          let status;
          try { status = await api(`/api/cicd/pd-conflicts/run-status?${qs}`, readOptions); }
          catch (error) {
            // One migration read for an older stored plan. Events never take this path.
            if (!active() || error.code !== "PD_CONFLICT_STATUS_LEGACY") throw error;
            const legacyQuery = runStatusQuery(spaceId, packageId, lr.resolveRunId, lr.planId, false, runId);
            status = await api(`/api/cicd/pd-conflicts/run-status?${legacyQuery}`, readOptions);
          }
          if (!active()) return;
          if (ACTIVE_RUN_STATUSES.has(status.status)) {
            // Lauf noch aktiv: gemeinsame Ereignisverbindung beobachten.
            resolveRunRef.current = status.id || lr.resolveRunId || null;
            planIdRef.current = lr.planId || null;
            setResult({ dispatchState: status.dispatchState, run: { id: status.id, html_url: status.html_url } });
            setRunning(true);
          } else if (status.status === "completed") {
            setRunning(false);
            completedReadRef.current = lr.planId || String(lr.resolveRunId);
            if (status.conclusion === "success") {
              setLastResolveStatus({ ...status, createdAt: lr.createdAt, runtimes: lr.runtimes });
            } else {
              // Fehlgeschlagen: Fehlerabschnitt anzeigen.
              setLastResolveStatus({ ...status, createdAt: lr.createdAt, runtimes: lr.runtimes });
            }
          }
        } catch (error) {
          if (active()) setStatusError(error);
        }
      } else if (!lr) {
        setLastResolveStatus(null);
      }
    } catch (error) {
      if (!active()) return;
      setReadError(error);
      setCanResolve(false);
    } finally {
      if (active()) setLoading(false);
    }
  }, [spaceId, packageId, runId, enabled, authToken]);

  useEffect(() => {
    setConflicts(null);
    setCanResolve(false);
    setReadError(null);
    setReadWarning(null);
    setStatusError(null);
    setLoading(false);
    setRunning(false);
    setResult(null);
    setLastResolveStatus(null);
    resolveRunRef.current = null;
    planIdRef.current = null;
    completedReadRef.current = null;
    fetchConflicts();
    return () => { readController.current?.abort(); readVersion.current += 1; };
  }, [fetchConflicts]);

  // Events invalidate the stored plan status only. They never trigger a SAP conflict
  // scan. The shared subscription reconciles lost events and coalesces overlapping reads.
  const statusController = useRef(null);
  useEffect(() => () => statusController.current?.abort(), [spaceId, packageId, runId, enabled, authToken]);
  const refreshStatus = useCallback(async ({ force = false } = {}) => {
    const resolveRunId = resolveRunRef.current;
    const planId = planIdRef.current;
    if ((!running && !force && !lastResolveStatus?.errorsUnavailable) || !enabled || (!resolveRunId && !planId)) return;
    const version = readVersion.current;
    const controller = new AbortController();
    statusController.current?.abort();
    statusController.current = controller;
    const active = () => mountedRef.current && !controller.signal.aborted && version === readVersion.current && currentToken() === authToken;
    try {
      const qs = runStatusQuery(spaceId, packageId, resolveRunId, planId, !force, runId);
      const data = await api(`/api/cicd/pd-conflicts/run-status?${qs}`, { signal: controller.signal, retry: false });
      if (!active()) return;
      setStatusError(null);
      if (data.id) resolveRunRef.current = data.id;
      setResult(previous => ({ ...previous, dispatchState: data.dispatchState,
        run: { ...(previous?.run || {}), id: data.id, html_url: data.html_url || previous?.run?.html_url } }));
      if (ACTIVE_RUN_STATUSES.has(data.status)) setRunning(true);
      if (data.status === "completed") {
        setRunning(false);
        setLastResolveStatus(data);
        const completionKey = planId || String(resolveRunId);
        // Delayed diagnostic details update the local status, without another SAP scan.
        if (completedReadRef.current !== completionKey) {
          completedReadRef.current = completionKey;
          await fetchConflicts({ resolvedStatus: data, completionPlanId: planId });
        }
      }
    } catch (error) {
      if (active()) setStatusError(error);
    }
  }, [running, lastResolveStatus?.errorsUnavailable, enabled, spaceId, packageId, runId, authToken, fetchConflicts]);
  const connection = useCicdEvents(spaceId, refreshStatus, (running || lastResolveStatus?.errorsUnavailable === true) && enabled,
    { localRevision: true, reconcileOnConnect: true });
  useEffect(() => {
    if (connection !== "forbidden") return;
    statusController.current?.abort();
    readController.current?.abort(); readVersion.current += 1;
    setConflicts(null); setCanResolve(false); setRunning(false);
    setReadError({ status: 403, code: "PD_CONFLICT_FORBIDDEN" });
  }, [connection]);

  const submit = useCallback(async (actions) => {
    const version = readVersion.current;
    const data = await api("/api/cicd/pd-conflicts/resolve", {
      method: "POST",
      body: JSON.stringify({ spaceId, packageId, runId, actions }),
    });
    // Accepted writes retain ownership after the dialog closes. Ignore only their late UI result.
    if (!mountedRef.current || version !== readVersion.current || currentToken() !== authToken) return data;
    // Lauf-ID oder Plan-ID für gespeicherte Statusabfragen merken.
    resolveRunRef.current = data?.run?.id ?? null;
    planIdRef.current = data?.planId ?? null;
    setResult(data);
    completedReadRef.current = null;
    setLastResolveStatus(null);
    setStatusError(null);
    setRunning(true);
    return data;
  }, [spaceId, packageId, runId, authToken]);

  const hasConflicts = conflicts !== null && conflicts.length > 0;

  return { scope: JSON.stringify([spaceId, packageId, runId]), conflicts, canResolve, loading, readError, readWarning, statusError, running, result, targetStage, lastResolveStatus, hasConflicts, submit, refresh: fetchConflicts, refreshStatus };
}

function PdConflictStatusNotice({ pdConflict }) {
  const { t } = useTranslation("freigabe");
  if (!pdConflict?.statusError) return null;
  return <Alert severity="warning"><Stack spacing={1} alignItems="flex-start">
    <Typography variant="body2">{t("stageBranches.pdConflict.statusUnavailable")}</Typography>
    <MonitorButton onClick={() => pdConflict.refreshStatus?.({ force: true })}>{t("stageBranches.pdConflict.readRetry")}</MonitorButton>
  </Stack></Alert>;
}

// Reuse the same observed read error in package details and the nested sync dialog.
export function PdConflictReadNotice({ error, onRetry }) {
  const { t } = useTranslation("freigabe");
  if (!error) return null;
  const inactive = [...new Set((error.issues || [])
    .filter(issue => issue.code === "EDGE_LOCATION_INACTIVE" && typeof issue.runtime === "string")
    .map(issue => issue.runtime))];
  const cloudClear = (error.runtimeChecks || []).some(check => check.runtime === "cloud"
    && check.state === "checked" && check.conflictCount === 0);
  return <Alert severity="warning" variant="outlined"
    sx={{ "& .MuiAlert-message": { minWidth: 0, width: "100%" } }}>
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="body2">{t(error.code === "TRANSPORT_READ_TIMEOUT" || error.name === "AbortError"
        ? "stageBranches.pdConflict.readTimeout" : "stageBranches.pdConflict.readFailed")}</Typography>
      {cloudClear && <Typography variant="body2">{t("stageBranches.pdConflict.cloudChecked")}</Typography>}
      {inactive.map(runtime => <Typography key={runtime} variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {t("stageBranches.pdConflict.edgeInactive", { runtime })}
      </Typography>)}
      {onRetry && <MonitorButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>{t("stageBranches.pdConflict.readRetry")}</MonitorButton>}
    </Stack>
  </Alert>;
}

/**
 * Group flat API conflicts by (agency, scheme, id, ownerPid); each group collects its runtimes.
 * Same triplet+owner on multiple runtimes = multiple entries in `runtimes`.
 * Without `runtime` field (v27 API) the entry defaults to "cloud".
 */
export function groupConflicts(conflicts) {
  const map = new Map();
  for (const c of conflicts || []) {
    const key = [c.agency, c.scheme, c.id, c.ownerPid, c.wantedPid, c.wantedId, c.conflictType].join("\0");
    if (!map.has(key)) {
      map.set(key, {
        key,
        agency: c.agency, scheme: c.scheme, id: c.id,
        ownerPid: c.ownerPid, wantedPid: c.wantedPid, wantedId: c.wantedId, conflictType: c.conflictType,
        runtimes: [],
      });
    }
    map.get(key).runtimes.push({
      runtime: c.runtime || "cloud",
      runtimeName: c.runtimeName || null,
      owner: c.owner, countError: c.countError, deleteAllowed: c.deleteAllowed,
      resolvable: c.resolvable, reason: c.reason,
      resolution: c.resolution || "remove",
      deprecateOld: c.deprecateOld,
    });
  }
  return [...map.values()];
}

/** Beschriftung für einen Runtime-Eintrag. */
function rtLabel(rt, t) {
  return rt.runtime === "cloud" ? t("stageBranches.pdConflict.runtimeCloudLabel") : (rt.runtimeName || rt.runtime);
}

/** Warntext für die Löschoption (v29: Löschen nur auf der Cloud). */
function deleteWarningForScope(group, scope, t) {
  return t("stageBranches.pdConflict.deleteWarning");
}

/**
 * Kartenzeile: nur ein kurzer Text, kein Knopf.
 * "Ursache: Konflikt im Partner Directory, in den Details lösen" oder "Bereinigung im Partner Directory läuft".
 */
export function PdConflictCardLine({ pdConflict }) {
  const { t } = useTranslation("freigabe");
  if (!pdConflict || pdConflict.loading) return null;
  if (pdConflict.running && pdConflict.hasConflicts) {
    return <Typography variant="body2" role="status" sx={{ color: v("accent"), overflowWrap: "anywhere" }}>
      {t(pdConflict.result?.dispatchState === "unknown"
          ? "stageBranches.pdConflict.dispatchUnknown" : "stageBranches.pdConflict.running")}
    </Typography>;
  }
  if (!pdConflict.hasConflicts) return null;
  return <Typography variant="body2" role="status" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
    {t("stageBranches.pdConflict.cardHint")}
  </Typography>;
}

/**
 * Fehlerabschnitt: zeigt Zusammenfassung und Zeilen einer fehlgeschlagenen Bereinigung.
 * MUI Alert severity="error", normale Schrift, umbrechend, Link zum Lauf, Zeitpunkt.
 */
function LastResolveFailedSection({ status, t, onRetry }) {
  if (!status || status.conclusion === "success") return null;
  const date = status.createdAt ? new Date(status.createdAt).toLocaleString() : null;
  return <Alert severity="error" variant="outlined" sx={{
    borderColor: "var(--iio-critical)", color: "var(--iio-text-1)", backgroundColor: "transparent",
    "& .MuiAlert-icon": { color: "var(--iio-critical)" },
  }}>
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t("stageBranches.pdConflict.lastResolveFailedTitle")}
      </Typography>
      {status.errorSummary && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {status.errorSummary}
      </Typography>}
      {status.errors?.length > 0 && status.errors.map((line, i) =>
        <Typography key={i} variant="body2" sx={{ overflowWrap: "anywhere", color: "var(--iio-text-2)" }}>
          {line}
        </Typography>)}
      {status.errorsUnavailable && <Typography variant="body2" sx={{ color: "var(--iio-text-2)" }}>
        {t("stageBranches.pdConflict.lastResolveErrorsUnavailable")}
      </Typography>}
      {status.errorsUnavailable && onRetry && <MonitorButton onClick={onRetry}>
        {t("stageBranches.pdConflict.readRetry")}
      </MonitorButton>}
      {status.html_url && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        <a href={status.html_url} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
          {t("stageBranches.pdConflict.viewRun")} <OpenInNewRounded sx={{ fontSize: 14, verticalAlign: "middle" }}/>
        </a>
      </Typography>}
      {date && <Typography variant="caption" sx={{ color: "var(--iio-text-3)" }}>
        {t("stageBranches.pdConflict.lastResolveFailedAt", { date })}
      </Typography>}
    </Stack>
  </Alert>;
}

/**
 * Abschnitt im Details-Dialog: Konflikte anzeigen und lösen.
 * Zeigt die Konfliktzeilen gruppiert nach (agency, scheme, id, ownerPid), Auswahl je Gruppe
 * (Umfang + Aktion), Bestätigung, Absende-Knopf, Lauf-Link und Running-Zustand.
 * v28: Unterstützung für mehrere Runtimes (Cloud + Edge).
 * Laufanzeige: stageLabelFn zur Beschriftung der Server-Stufe, Fehlerabschnitt bei lastResolve-Fehlschlag.
 */
export function PdConflictSection({ pdConflict, targetStageLabel, stageLabelFn, hideLoading = false }) {
  const { t } = useTranslation("freigabe");
  const [choices, setChoices] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [localResult, setLocalResult] = useState(null);
  // Welche Runtimes beim letzten Absenden betroffen waren (für den Hinweis nach dem Lauf).
  const [resolvedScopes, setResolvedScopes] = useState(null);

  const conflictScope = JSON.stringify([pdConflict?.scope, (pdConflict?.conflicts || []).map(c =>
    [c.agency, c.scheme, c.id, c.ownerPid, c.wantedPid, c.wantedId, c.runtime, c.resolvable, c.deleteAllowed, c.countError, c.owner])]);
  useEffect(() => { setChoices({}); setConfirmed(false); setError(null); }, [conflictScope]);

  if (pdConflict?.loading) return hideLoading ? null : <Stack spacing={1} aria-busy="true">
    <Typography>{t("stageBranches.pdConflict.readLoading")}</Typography>
    <LinearProgress aria-label={t("stageBranches.pdConflict.readLoading")}/>
  </Stack>;
  if (pdConflict?.readError) return <PdConflictReadNotice error={pdConflict.readError} onRetry={pdConflict.refresh}/>;
  if (!pdConflict) return null;
  if (!pdConflict.hasConflicts && !pdConflict.running) return <Stack spacing={1}>
    <PdConflictReadNotice error={pdConflict.readWarning} onRetry={pdConflict.refresh}/>
    <PdConflictStatusNotice pdConflict={pdConflict}/>
    <LastResolveFailedSection status={pdConflict.lastResolveStatus} t={t} onRetry={pdConflict.statusError ? undefined : () => pdConflict.refreshStatus?.({ force: true })}/>
    {pdConflict.lastResolveStatus?.conclusion === "success" && <Typography role="status" variant="body2">
      {t("stageBranches.pdConflict.resolvedTransportPending")}
    </Typography>}
  </Stack>;

  const { conflicts, canResolve, running, result, lastResolveStatus } = pdConflict;
  // Stufe aus der Server-Antwort (targetStage) hat Vorrang; stageLabelFn übersetzt den Schlüssel
  // in die Board-Beschriftung. Fallback auf den bisherigen targetStageLabel (Karten-Prop).
  const serverStage = pdConflict.targetStage;
  const stageText = serverStage
    ? (typeof stageLabelFn === "function" ? stageLabelFn(serverStage) : serverStage)
    : (targetStageLabel || "");
  const shownResult = localResult || result;
  const groups = groupConflicts(conflicts);

  const updateChoice = (key, patch) => { setConfirmed(false); setChoices(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } })); };

  const doSubmit = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const scopes = new Set();
      const rawActions = groups.flatMap((group, i) => {
        const choice = { action: "remove", scope: "all", ...choices[group.key] };
        const selectedRts = choice.scope === "all"
          ? group.runtimes.filter(r => r.resolvable !== false)
          : group.runtimes.filter(r => r.runtime === choice.scope && r.resolvable !== false);
        if (!selectedRts.length) return [];
        selectedRts.forEach(r => scopes.add(r.runtime === "cloud" ? "cloud" : "edge"));
        return selectedRts.flatMap(rt => {
          // v29: Edge Runtimes nutzen reassign + optionales mark-deprecated
          if (rt.runtime !== "cloud") {
            const reassign = {
              type: "reassign-alternative-partner", runtime: rt.runtime,
              pid: group.ownerPid, toPid: group.wantedPid,
              agency: group.agency, scheme: group.scheme, id: group.id,
            };
            return rt.deprecateOld !== false
              ? [reassign, { type: "mark-deprecated", runtime: rt.runtime, pid: group.ownerPid }]
              : [reassign];
          }
          // Cloud: remove + optionales delete (unverändert)
          const remove = { type: "remove-alternative-partner", runtime: rt.runtime, pid: group.ownerPid, agency: group.agency, scheme: group.scheme, id: group.id };
          return choice.action === "delete" && rt.deleteAllowed !== false && !rt.countError
            ? [remove, { type: "delete-partner", runtime: rt.runtime, pid: group.ownerPid }]
            : [remove];
        });
      });
      // Dedupliziere delete-partner je (runtime, pid).
      const seen = new Set();
      const actions = [];
      for (const action of rawActions) {
        if (action.type === "delete-partner" || action.type === "mark-deprecated") {
          const key = `${action.type}\0${action.runtime}\0${action.pid}`;
          if (seen.has(key)) continue;
          seen.add(key);
        }
        actions.push(action);
      }
      setResolvedScopes(scopes);
      const data = await pdConflict.submit(actions);
      setLocalResult(data);
      // Nach dem Lauf wird für verbleibende Konflikte (Teillösung) neu ausgewählt und neu bestätigt.
      setChoices({});
      setConfirmed(false);
    } catch (failure) {
      setError(failure?.message || t("stageBranches.pdConflict.resolveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasResolvable = groups.some(g => g.runtimes.some(r => r.resolvable !== false));

  // Running-Zustand: zeige Status + Link + Hinweis je Umfang
  if (running) {
    const scopes = resolvedScopes;
    return <Stack component="section" spacing={1} aria-label={t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}>
      <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), m: 0 }}>
        {t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}
      </Typography>
      <Typography variant="body2" role="status" sx={{ color: v("accent") }}>
        {t(pdConflict.result?.dispatchState === "unknown"
          ? "stageBranches.pdConflict.dispatchUnknown" : "stageBranches.pdConflict.running")}
      </Typography>
      <PdConflictStatusNotice pdConflict={pdConflict}/>
      {pdConflict.result?.dispatchState === "unknown" && <>
        <Typography variant="body2">{t("stageBranches.pdConflict.dispatchUnknownHint")}</Typography>
        {!pdConflict.statusError && <MonitorButton onClick={() => pdConflict.refreshStatus?.({ force: true })}>
          {t("stageBranches.pdConflict.readRetry")}
        </MonitorButton>}
      </>}
      {shownResult?.run?.html_url && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        <a href={shownResult.run.html_url} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
          {t("stageBranches.pdConflict.viewRun")} <OpenInNewRounded sx={{ fontSize: 14, verticalAlign: "middle" }}/>
        </a>
      </Typography>}
      {scopes ? <>
        {scopes.has("cloud") && <Typography variant="body2" sx={{ color: v("text-2") }}>
          {t("stageBranches.pdConflict.runningRetryHintCloud")}
        </Typography>}
        {scopes.has("edge") && <Typography variant="body2" sx={{ color: v("text-2") }}>
          {t("stageBranches.pdConflict.runningRetryHintEdge")}
        </Typography>}
      </> : <Typography variant="body2" sx={{ color: v("text-2") }}>
        {t("stageBranches.pdConflict.runningRetryHint")}
      </Typography>}
    </Stack>;
  }

  return <Stack component="section" spacing={1.5} aria-label={t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}>
    <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), m: 0 }}>
      {t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}
    </Typography>
    <PdConflictReadNotice error={pdConflict.readWarning} onRetry={pdConflict.refresh}/>
    <PdConflictStatusNotice pdConflict={pdConflict}/>
    {/* Fehlerabschnitt einer vorherigen fehlgeschlagenen Bereinigung (Laufanzeige). */}
    <LastResolveFailedSection status={lastResolveStatus} t={t} onRetry={pdConflict.statusError ? undefined : () => pdConflict.refreshStatus?.({ force: true })}/>
    {lastResolveStatus?.conclusion === "success" && <Typography role="status" variant="body2">{t("stageBranches.pdConflict.resolvedTransportPending")}</Typography>}
    <Typography variant="body2" sx={{ color: v("text-2") }}>
      {t("stageBranches.pdConflict.dialogIntro", { stage: stageText })}
    </Typography>
    <Stack component="ul" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
      {groups.map((group, index) => {
        const resolvableRts = group.runtimes.filter(r => r.resolvable !== false);
        const notResolvableRts = group.runtimes.filter(r => r.resolvable === false);
        const noneResolvable = resolvableRts.length === 0;
        const storedChoice = { action: "remove", scope: resolvableRts.length > 1 ? "all" : (resolvableRts[0]?.runtime || "cloud"), ...choices[group.key] };
        const choice = { ...storedChoice, action: storedChoice.action === "delete" && resolvableRts.some(rt => rt.runtime === "cloud" && (rt.deleteAllowed === false || rt.countError)) ? "remove" : storedChoice.action };
        // v29: Cloud und Edge getrennt im gewählten Umfang
        const scopeRts = choice.scope === "all"
          ? resolvableRts
          : resolvableRts.filter(r => r.runtime === choice.scope);
        const cloudInScope = scopeRts.filter(r => r.runtime === "cloud");
        const edgeInScope = scopeRts.filter(r => r.runtime !== "cloud");
        return <li key={group.key}>
          <Stack spacing={1} sx={{ borderTop: index > 0 ? `1px solid ${v("border-subtle")}` : undefined, pt: index > 0 ? 1.5 : 0 }}>
            {/* Konfliktzeile */}
            <Typography variant="body2" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              {t(group.conflictType === "alias" ? "stageBranches.pdConflict.aliasConflictLine" : "stageBranches.pdConflict.conflictLine", {
                agency: group.agency, id: group.id, scheme: group.scheme,
                stage: stageText, ownerPid: group.ownerPid, wantedPid: group.wantedPid, wantedId: group.wantedId,
              })}
            </Typography>
            {/* Owner-Zählung je Runtime */}
            {group.runtimes.length > 1
              ? group.runtimes.map(rt => rt.owner && !rt.countError && <Typography key={rt.runtime} variant="body2" sx={{ color: v("text-2") }}>
                  {t("stageBranches.pdConflict.ownerCountRuntime", {
                    pid: group.ownerPid, runtimeLabel: rtLabel(rt, t),
                    strings: rt.owner.strings ?? 0, binaries: rt.owner.binaries ?? 0,
                    alternativePartners: rt.owner.alternativePartners ?? 0, authorizedUsers: rt.owner.authorizedUsers ?? 0,
                  })}
                </Typography>)
              : group.runtimes[0]?.owner && !group.runtimes[0]?.countError && <Typography variant="body2" sx={{ color: v("text-2") }}>
                  {t("stageBranches.pdConflict.ownerCount", {
                    pid: group.ownerPid,
                    strings: group.runtimes[0].owner.strings ?? 0, binaries: group.runtimes[0].owner.binaries ?? 0,
                    alternativePartners: group.runtimes[0].owner.alternativePartners ?? 0, authorizedUsers: group.runtimes[0].owner.authorizedUsers ?? 0,
                  })}
                </Typography>}
            {group.runtimes.filter(rt => rt.countError).map(rt => <Typography key={rt.runtime} variant="body2" sx={{ color: v("warning") }}>
              {t("stageBranches.pdConflict.ownerCountUnavailable", { pid: group.ownerPid, runtimeLabel: rtLabel(rt, t) })}
            </Typography>)}
            {group.runtimes.some(rt => rt.runtime === "cloud" && rt.deleteAllowed === false && !rt.countError) && <Typography variant="body2" sx={{ color: v("text-2") }}>
              {t("stageBranches.pdConflict.deleteProtected")}
            </Typography>}
            {/* Nicht lösbare Runtimes (falls manche lösbar, manche nicht) */}
            {notResolvableRts.length > 0 && resolvableRts.length > 0 && notResolvableRts.map(rt => <Typography key={rt.runtime} variant="body2" sx={{ color: v("warning") }}>
              {t("stageBranches.pdConflict.notResolvableRuntime", { runtimeLabel: rtLabel(rt, t) })}
            </Typography>)}
            {/* Umfang: Nur anbieten wenn canResolve und mehr als eine lösbare Runtime */}
            {canResolve && resolvableRts.length > 1 ? <RadioGroup value={choice.scope} onChange={e => updateChoice(group.key, { scope: e.target.value })}
              aria-label={t("stageBranches.pdConflict.scopeAll")}>
              {resolvableRts.some(r => r.runtime === "cloud") && <FormControlLabel value="cloud"
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeCloud")}</Typography>}/>}
              {resolvableRts.filter(r => r.runtime !== "cloud").map(rt => <FormControlLabel key={rt.runtime} value={rt.runtime}
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeEdge", { name: rt.runtimeName || rt.runtime })}</Typography>}/>)}
              <FormControlLabel value="all"
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeAll")}</Typography>}/>
            </RadioGroup> : resolvableRts.length === 1 ? <Typography variant="body2" sx={{ color: v("text-2") }}>
              {resolvableRts[0].runtime === "cloud"
                ? t("stageBranches.pdConflict.runtimeCloud")
                : t("stageBranches.pdConflict.runtimeEdge", { name: resolvableRts[0].runtimeName || resolvableRts[0].runtime })}
            </Typography> : null}
            {/* Komplett nicht lösbar */}
            {noneResolvable ? <Typography variant="body2" sx={{ color: v("warning") }}>
              {t("stageBranches.pdConflict.notResolvable")}
            </Typography> : canResolve ? <>
              {/* v29: Aktionswahl nur für Cloud Runtimes im gewählten Umfang */}
              {cloudInScope.length > 0 && <RadioGroup value={choice.action} onChange={e => updateChoice(group.key, { action: e.target.value })}>
                <FormControlLabel value="remove" control={<Radio size="small" sx={RADIO_SX}/>}
                  label={<Typography variant="body2">{t("stageBranches.pdConflict.optionRemove", { ownerPid: group.ownerPid })}</Typography>}/>
                <FormControlLabel value="delete" disabled={cloudInScope.some(rt => rt.deleteAllowed === false || rt.countError)} control={<Radio size="small" sx={RADIO_SX}/>}
                  label={<Stack spacing={0.25}>
                    <Typography variant="body2">{t("stageBranches.pdConflict.optionDelete", { ownerPid: group.ownerPid })}</Typography>
                    <Typography variant="caption" sx={{ color: v("danger") }}>{deleteWarningForScope(group, choice.scope, t)}</Typography>
                  </Stack>}/>
              </RadioGroup>}
              {/* v29: Edge Erklärungstext je Edge Runtime im Umfang */}
              {edgeInScope.map(rt => <Typography key={rt.runtime} variant="body2" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>
                {rt.deprecateOld !== false
                  ? t("stageBranches.pdConflict.edgeReassignExplanation", {
                      name: rt.runtimeName || rt.runtime, agency: group.agency, scheme: group.scheme,
                      id: group.id, wantedPid: group.wantedPid, ownerPid: group.ownerPid,
                    })
                  : t("stageBranches.pdConflict.edgeReassignExplanationNoDeprecate", {
                      name: rt.runtimeName || rt.runtime, agency: group.agency, scheme: group.scheme,
                      id: group.id, wantedPid: group.wantedPid, ownerPid: group.ownerPid,
                    })}
              </Typography>)}
            </> : null}
          </Stack>
        </li>;
      })}
    </Stack>
    {canResolve && <>
      <FormControlLabel
        control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
          size="small" sx={{ color: "var(--iio-text-2)", "&.Mui-checked": { color: "var(--iio-signal)" } }}/>}
        label={<Typography variant="body2">{t("stageBranches.pdConflict.confirm")}</Typography>}/>
      {error && <Alert severity="error" variant="outlined" sx={{
        borderColor: "var(--iio-critical)", color: "var(--iio-text-1)", backgroundColor: "transparent",
        "& .MuiAlert-icon": { color: "var(--iio-critical)" },
      }}>{error}</Alert>}
      <MonitorButton variant="contained" disableElevation disabled={!confirmed || submitting || !hasResolvable}
        onClick={doSubmit} sx={{ alignSelf: "flex-start" }}>
        {submitting ? t("stageBranches.pdConflict.submitting") : t("stageBranches.pdConflict.submitAction")}
      </MonitorButton>
    </>}
  </Stack>;
}

// Rückwärtskompatibel: Default-Export für den Fall, dass ein Import noch den alten Namen nutzt.
export default function PdConflictResolver() { return null; }
